import { theme } from 'common/selectCustomStyles';
import React, { useState, useEffect, useRef } from 'react';
import ToggleSwitch from './ToggleSwitch';
import Select from "react-select";
import SubNationalSelector from './SubNationalSelector';
import { requiresPolicyType, supportsSubNational, getSubNationalLabel } from 'services/subNationalService';
import { strategyVersionOptions } from 'constants/global';

function Step1({
  isEditMode, 
  formData, 
  setFormData, 
  handleInputChange, 
  setCurrentStep, 
  countryOptions, 
  stageOptions, 
  systemOptions, 
  isLoading, 
  errors, 
  milestoneOptions,
  subNationalOptions,
  policyTypeOptions,
  isAGRAUser
}) {
    // Local state for validation errors
    const [validationErrors, setValidationErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};
        
        // Validate Policy Name
        if (!formData.policy_request_body?.name?.trim()) {
            newErrors.name = 'Policy name is required';
        } else if (formData.policy_request_body.name.length > 100) {
            newErrors.name = 'Policy name must be less than 100 characters';
        }

        // Validate Country
        if (!formData.policy_request_body?.country_id) {
            newErrors.country = 'Please select a country';
        }

        // Validate Policy Type (for countries that support it)
        if (requiresPolicyType(formData.policy_request_body?.country_id) && !formData.policy_request_body?.policy_type_id) {
            newErrors.policyTypeID = 'Please select a policy type';
        }
        
        // Validate Policy Scope (for countries that support it)
        if (supportsSubNational(formData.policy_request_body?.country_id) && !formData.policy_request_body?.policy_scope) {
            newErrors.policyScope = 'Please select a policy scope';
        }

        // Validate Sub-national selection if policy scope is Sub-national
        const isSubNational = formData.policy_request_body?.policy_scope === "Sub-national";
        
        if (isSubNational && supportsSubNational(formData.policy_request_body?.country_id) && !formData.policy_request_body?.sub_national_id) {
          newErrors.subNationalID = `Please select a ${getSubNationalLabel(formData.policy_request_body?.country_id).toLowerCase()}`;
        }

        // Validate Stage
        if (!formData.policy_request_body?.stage_id) {
            newErrors.stage = 'Please select a stage';
        }

        // Validate Systems
        if (!formData.system_ids?.length) {
            newErrors.systems = 'Please select at least one system';
        }

        if (formData.policy_request_body.is_agra_supported && !formData.policy_request_body.agra_strategy_version) { //TODO: Add check for agra user
          newErrors.agraStrategyVersion = 'Please select a strategy version';
        }

        setValidationErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleToggleChange = (field) => {
      setFormData(prev => ({
        ...prev,
        policy_request_body: {
          ...prev.policy_request_body,
          [field]: !formData.policy_request_body[field]
        }
      }));
    };

    const handleIsAgraSupportedChange = (field) => {
      setFormData(prev => ({
        ...prev,
        policy_request_body: {
          ...prev.policy_request_body,
          [field]: !formData.policy_request_body[field],
          agra_strategy_version: null
        }
      })); 
    } 
    
    const handleSystemChange = (systems) => {  
      setFormData(prev => ({
        ...prev,
        system_ids: systems.map((option => {
          return {id: parseInt(option.value)};
        }))
      }));
      // Clear system validation error when systems are selected
      if (systems.length > 0) {
        setValidationErrors(prev => ({ ...prev, systems: undefined }));
      }
    };

    const handleMilestonesCompletedChange = (milestones) => {  
      setFormData(prev => ({
        ...prev,
        current_stage_completed_milestones_ids: milestones.map((option => {
          return {milestone_id: parseInt(option.value)};
        }))
      }));
    };

    const handleNameChange = (name, value) => {
      handleInputChange(name, value);
      // Clear name validation error when user types
      setValidationErrors(prev => ({ ...prev, name: undefined }));
    };

    const getDefaultValue = (options, value, isMulti = "") => {
      if (!options || options.length === 0 || !value) return isMulti ? [] : null;
      if (isMulti) {
        return options.filter(option => 
          value.some(v => parseInt(option.value) === (isMulti === "systems" ? v.id : v.milestone_id))
        );
      }
      return options.find(option => option.value === value) || null;
    };

    const handleNext = () => {
      const isValid = validateForm();
      if (isValid) {
        setCurrentStep(2);
      }
    };

    return (
        <div className="space-y-4">
          <div>
            <label className="block mb-1 text-sm">Policy Name*</label>
            <input
              type="text"
              name="name"
              value={formData.policy_request_body?.name}
              onChange={(e) => handleNameChange(e.target.name, e.target.value)}
              className={`w-full p-2 border rounded`}
              placeholder="Enter policy name"
            />
            {validationErrors.name && (
              <span className="text-red-500 text-sm mt-1 ml-1">{validationErrors.name}</span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center">
              <label className="block text-sm">Country*</label>
              <div className="ms-2" style={{ width: "100%" }}>
                <Select
                  options={countryOptions}
                  isDisabled={isEditMode}
                  value={getDefaultValue(
                    countryOptions,
                    formData.policy_request_body.country_id
                  )}
                  className={`basic-single`}
                  isLoading={isLoading.countryOptions}
                  classNamePrefix="select"
                  placeholder="Select a country"
                  theme={theme}
                  onChange={(option) => {
                    handleInputChange("country_id", option.value);
                    setValidationErrors(prev => ({ ...prev, country: undefined }));
                  }}
                />
                {(errors.countryOptions || validationErrors.country) && (
                  <span className="text-red-500 text-sm mt-1">
                    {errors.countryOptions || validationErrors.country}
                  </span>
                )}
              </div>
            </div>
            
            <SubNationalSelector
              formData={formData}
              handleInputChange={handleInputChange}
              setFormData={setFormData}
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
              getDefaultValue={getDefaultValue}
              isLoading = {isLoading}
              errors = {errors}
              subNationalOptions = {subNationalOptions}
              policyTypeOptions = {policyTypeOptions}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center">
              <label className="block text-sm">Stage*</label>
              <div className="ms-2" style={{ width: "100%" }}>
                <Select
                  options={stageOptions}
                  isDisabled={isEditMode}
                  value={getDefaultValue(
                    stageOptions,
                    formData.policy_request_body.stage_id
                  )}
                  className={`basic-single`}
                  isLoading={isLoading.stageOptions}
                  classNamePrefix="select"
                  placeholder="Select a stage"
                  theme={theme}
                  onChange={(option) => {
                    handleInputChange("stage_id", option.value);
                    setValidationErrors(prev => ({ ...prev, stage: undefined }));
                    
                    // Reset is_complete if stage is not 6
                    if (option.value != 6 && formData.policy_request_body.is_complete) {
                      handleInputChange('is_complete', false);
                    }
                  }}
                />
                {(errors.stageOptions || validationErrors.stage) && (
                  <span className="text-red-500 text-sm mt-1">
                    {errors.stageOptions || validationErrors.stage}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center">
              <label className="block text-sm">Systems*</label>
              <div className="ms-2" style={{ width: "100%" }}>
                <Select
                  isMulti
                  options={systemOptions}
                  value={getDefaultValue(systemOptions, formData.system_ids, "systems")}
                  className={`basic-single`}
                  isLoading={isLoading.systemOptions}
                  classNamePrefix="select"
                  placeholder="Select systems"
                  theme={theme}
                  onChange={handleSystemChange}
                />
                {(errors.systemOptions || validationErrors.systems) && (
                  <span className="text-red-500 text-sm mt-1">
                    {errors.systemOptions || validationErrors.systems}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {!isEditMode && formData.policy_request_body.stage_id === 6 && (
              <ToggleSwitch
                label="Is policy complete?"
                defaultChecked={formData.policy_request_body.is_complete}
                onChange={() => handleToggleChange('is_complete')}
              />
            )}
            {!isEditMode && !formData.policy_request_body.is_complete && <div className="flex items-center">
              <label className="block text-sm">Milestones Completed In Current Stage</label>
              <div className="ms-2" style={{ width: "100%" }}>
                <Select
                  isMulti
                  isDisabled={isEditMode}
                  options={milestoneOptions}
                  value={getDefaultValue(milestoneOptions, formData.current_stage_completed_milestones_ids, "milestones_completed")}
                  className={`basic-single`}
                  isLoading={isLoading.milestoneOptions}
                  classNamePrefix="select"
                  placeholder="Select milestones"
                  theme={theme}
                  onChange={handleMilestonesCompletedChange}
                />
                {(errors.milestoneOptions) && (
                  <span className="text-red-500 text-sm mt-1">
                    {errors.milestoneOptions}
                  </span>
                )}
              </div>
            </div>}
          </div>

          <ToggleSwitch
            label="Is policy off track?"
            defaultChecked={formData.policy_request_body.is_off_track}
            onChange={() => handleToggleChange('is_off_track')}
          />
          
          <ToggleSwitch
            label="Is policy delayed?"
            defaultChecked={formData.policy_request_body.is_delayed}
            onChange={() => handleToggleChange('is_delayed')}
          />

          <ToggleSwitch
            label="Does policy support flagship?"
            defaultChecked={formData.policy_request_body.supports_flagship}
            onChange={() => handleToggleChange('supports_flagship')}
          />

          {isAGRAUser &&
            <div className='grid grid-cols-2 gap-4'>
              <ToggleSwitch
                label="Is AGRA supported?"
                defaultChecked={formData.policy_request_body.is_agra_supported}
                onChange={() => handleIsAgraSupportedChange('is_agra_supported')}
              /> 

              {formData.policy_request_body.is_agra_supported &&
                <div className="flex items-center">
                  <label className="block text-sm">Strategy Version*</label>
                  <div className="ms-2" style={{ width: "100%" }}>
                  <Select
                    options={strategyVersionOptions}
                    value={getDefaultValue(
                      strategyVersionOptions,
                      formData.policy_request_body.agra_strategy_version
                    )}
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select AGRA strategy version"
                    theme={theme}
                    onChange={(option) => {
                      handleInputChange("agra_strategy_version", option.value);
                      setValidationErrors(prev => ({ ...prev, agraStrategyVersion: undefined }));
                    }}
                  />
                  {validationErrors?.agraStrategyVersion && (
                    <span className="text-red-500 text-sm mt-1">
                      {validationErrors.agraStrategyVersion}
                    </span>
                  )}
                  </div>
                </div>
              }
            </div>
          }

          <div className="flex justify-end mt-6">
            <button
              onClick={handleNext}
              className="px-6 py-2 bg-green-100 text-green-800 rounded hover:bg-green-200"
            >
              Next
            </button>
          </div>
        </div>
    );
}

export default Step1;